import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import * as LucideIcons from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { useTranslation } from 'react-i18next';
import { useAppContext } from '../AppContext';
import { useLocationFilter } from '../context/LocationFilterContext';
import LoadingSpinner from './LoadingSpinner';
import CommentSection from './CommentSection';
import CommentModal from './CommentModal';
import BadgeIconStrip from './BadgeIconStrip';
import VisionCreator from './visions/VisionCreator';
import withVisionAura from './visions/withVisionAura';
import ReelItemSkeleton from './ReelItemSkeleton';
import useOnScreen from '../hooks/useOnScreen';
import { ReportModal } from './ReportModal'; // <-- ADD THIS IMPORT
import './Moments.css';
import ReelItem from './ReelItem';

const MemoizedReelItem = React.memo(({ moment, onVisible, ...props }) => {
    const [ref, isVisible] = useOnScreen({ threshold: 0.5 });

    useEffect(() => {
        if (isVisible) {
            onVisible(props.index);
        }
    }, [isVisible, onVisible, props.index]);

    return (
        <div ref={ref} className="reel-item">
            <ReelItem moment={moment} {...props} />
        </div>
    );
});

function MomentsFeedPage() {
    const { appFunctions, setMessage, handleUserSelect, showConfirmation, userId, handlePageChange, LucideIcons, db, appId, currentUserProfile, userProfiles } = useAppContext();
    const { t, i18n } = useTranslation();
    const { filterType, locationFilterRadius, userLocation, isLocationLoading, languagePreference, isProfileSyncComplete } = useLocationFilter();

    const [feed, setFeed] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentReelIndex, setCurrentReelIndex] = useState(0);
    const viewerRef = useRef(null);
    const [isMuted, setIsMuted] = useState(true);
    const [lastVisible, setLastVisible] = useState(null);
    const [hasMore, setHasMore] = useState(true);
    const isFetching = useRef(false);
    const [visionCreatorOpen, setVisionCreatorOpen] = useState(false);
    const [sharedContent, setSharedContent] = useState(null);
    const [showNewMomentsButton, setShowNewMomentsButton] = useState(false);
    const [seenMomentIds, setSeenMomentIds] = useState(new Set());
    const isInitialLoad = useRef(true);
    const [showReportModal, setShowReportModal] = useState(false);
    const [reportContent, setReportContent] = useState(null);
    const CACHE_KEY = `momentsFeedCache_${userId}`;

    const fetchMoments = useCallback(async (loadMore = false, isBackgroundRefresh = false) => {
        if (isFetching.current || (loadMore && !hasMore) || isLocationLoading || !isProfileSyncComplete) return;

        isFetching.current = true;
        if (!isBackgroundRefresh) {
            setIsLoading(true);
        }
        setError(null);

        if (filterType === 'local' && !userLocation) {
            setError(t('momentsFeed.locationError'));
            setFeed([]);
            setHasMore(false);
            setIsLoading(false);
            isFetching.current = false;
            return;
        }

        try {
            const getMoments = httpsCallable(appFunctions, 'getMomentsFeed');
            const result = await getMoments({
                lastVisible: loadMore ? lastVisible : null,
                seenMomentIds: Array.from(seenMomentIds),
                filterType: filterType,
                locationData: {
                    latitude: userLocation?.latitude,
                    longitude: userLocation?.longitude,
                    radius: locationFilterRadius,
                    country: currentUserProfile?.country
                },
                languagePreference: {
                    enabled: languagePreference,
                    language: i18n.language
                }
            });

            const { moments: newMoments, lastVisible: newLastVisible } = result.data;

            if (newMoments && newMoments.length > 0) {
                const momentsWithContentType = newMoments.map(m => ({ ...m, contentType: 'moment' }));

                if (isBackgroundRefresh) {
                    const currentFeedIds = new Set(feed.map(m => m.id));
                    const trulyNewMoments = momentsWithContentType.filter(m => !currentFeedIds.has(m.id));
                    if (trulyNewMoments.length > 0) {
                        setShowNewMomentsButton(true);
                    }
                } else {
                    setFeed(prev => loadMore ? [...prev, ...momentsWithContentType] : momentsWithContentType);
                    if (!loadMore) {
                        localStorage.setItem(CACHE_KEY, JSON.stringify(momentsWithContentType.slice(0, 5)));
                    }
                }
                
                setSeenMomentIds(prev => new Set([...prev, ...newMoments.map(m => m.id)]));
                setLastVisible(newLastVisible);
                setHasMore(newMoments.length > 0 && newLastVisible !== null);
            } else if (!loadMore) {
                setFeed([]); // Clear feed if no moments are returned on initial load
                setHasMore(false);
            } else {
                setHasMore(false);
            }
        } catch (err) {
            console.error("Error fetching intelligent feed:", err);
            setError(t('momentsFeed.loadError'));
            setMessage(t('momentsFeed.loadErrorLong', { message: err.message }));
        } finally {
            setIsLoading(false);
            isFetching.current = false;
            isInitialLoad.current = false;
        }
    }, [
        appFunctions, hasMore, lastVisible, seenMomentIds, filterType, 
        userLocation, locationFilterRadius, currentUserProfile, 
        languagePreference, i18n.language, setMessage, t, isLocationLoading, feed, CACHE_KEY
    ]);
    
    const filteredFeed = useMemo(() => {
        const getDistance = (loc1, loc2) => {
            const R = 6371; // Radius of the Earth in km
            const dLat = (loc2.latitude - loc1.latitude) * Math.PI / 180;
            const dLon = (loc2.longitude - loc1.longitude) * Math.PI / 180;
            const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
                      Math.cos(loc1.latitude * Math.PI / 180) * Math.cos(loc2.latitude * Math.PI / 180) *
                      Math.sin(dLon / 2) * Math.sin(dLon / 2);
            const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
            return R * c;
        };

        return feed.filter(post => {
            const author = userProfiles.find(p => p.id === post.authorId);
            if (author?.isAI) return true;

            if (filterType === 'local' && userLocation) {
                if (!author?.location) return false;
                const distance = getDistance(userLocation, author.location);
                return distance <= locationFilterRadius;
            }
            if (filterType === 'country') {
                return author?.country === currentUserProfile?.country;
            }
            return true;
        });
    }, [feed, userProfiles, filterType, userLocation, locationFilterRadius, currentUserProfile]);

    const handleShareToVision = (moment) => {
        setSharedContent(moment);
        setVisionCreatorOpen(true);
    };

    const handleReport = (moment) => {
        setReportContent({ contentId: moment.id, contentType: 'moment', authorId: moment.authorId });
        setShowReportModal(true);
    };
    
    const scrollToTop = () => {
        setFeed([]);
        setSeenMomentIds(new Set());
        setLastVisible(null);
        setHasMore(true);
        isInitialLoad.current = true;
        localStorage.removeItem(CACHE_KEY);
        fetchMoments(false);
        if (viewerRef.current) {
            viewerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
        }
        setShowNewMomentsButton(false);
    };

    useEffect(() => {
        // Load from cache first
        try {
            const cachedData = localStorage.getItem(CACHE_KEY);
            if (cachedData) {
                const parsedData = JSON.parse(cachedData);
                setFeed(parsedData);
                setSeenMomentIds(new Set(parsedData.map(m => m.id)));
                setIsLoading(false);
                // Fetch fresh data in the background
                fetchMoments(false, true);
            } else {
                // No cache, normal fetch
                fetchMoments(false);
            }
        } catch (e) {
            console.error("Failed to read cache", e);
            fetchMoments(false);
        }
    }, [filterType, locationFilterRadius, languagePreference]);

    const handleVisibilityChange = useCallback((index) => {
        setCurrentReelIndex(index);
    }, []);

    useEffect(() => {
        if (!isLoading && hasMore && feed.length > 0 && currentReelIndex >= filteredFeed.length - 2) {
            fetchMoments(true);
        }
    }, [currentReelIndex, filteredFeed.length, hasMore, isLoading, fetchMoments]);

    const handleLike = useCallback(async (momentId, authorId) => {
        const originalFeed = [...feed];
        const hasLiked = feed.find(m => m.id === momentId)?.likes?.includes(userId);
        
        // Optimistic update
        setFeed(prev => prev.map(m => {
            if (m.id === momentId) {
                return { ...m, likes: hasLiked ? m.likes.filter(id => id !== userId) : [...(m.likes || []), userId], likesCount: hasLiked ? (m.likesCount || 1) - 1 : (m.likesCount || 0) + 1 };
            }
            return m;
        }));

        try {
            const updateReaction = httpsCallable(appFunctions, 'updateReaction');
            await updateReaction({ entryId: momentId, authorId, reaction: 'like', contentType: 'moment' });

            const likedMomentRef = doc(db, `artifacts/${appId}/users/${userId}/likedMoments`, momentId);
            if (!hasLiked) {
                await setDoc(likedMomentRef, { timestamp: serverTimestamp() });
            }
        } catch (error) {
            console.error("Error syncing 'like' reaction:", error);
            setMessage(t('momentsFeed.likeError'));
            // Revert on failure
            setFeed(originalFeed);
        }
    }, [appFunctions, setMessage, userId, t, feed, db, appId]);

    const handleAmplify = useCallback((momentId) => {
        showConfirmation({
            message: t('momentsFeed.amplifyConfirmation'),
            onConfirm: async () => {
                const originalFeed = [...feed];
                
                // Optimistic update
                setFeed(prev => prev.map(m => {
                    if (m.id === momentId) {
                        const newEchoesInvested = (m.echoesInvested || 0) + 10;
                        const newMoment = { ...m, echoesInvested: newEchoesInvested };
                        if (newEchoesInvested >= 30) {
                            newMoment.revealedTo = [...(m.revealedTo || []), userId];
                        }
                        return newMoment;
                    }
                    return m;
                }));

                try {
                    const amplifyMoment = httpsCallable(appFunctions, 'amplifyMoment');
                    await amplifyMoment({ momentId: momentId, amount: 10 });
                    setMessage(t('momentsFeed.amplifySuccess'));
                } catch (error) {
                    setMessage(t('momentsFeed.amplifyFailed', { message: error.message }));
                    // Revert on failure
                    setFeed(originalFeed);
                }
            }
        });
    }, [appFunctions, setMessage, showConfirmation, userId, t, feed]);

    const FeedHeader = () => {
        const { filterType, locationFilterRadius } = useLocationFilter();
        const { currentUserProfile } = useAppContext();

        if (filterType === 'local') {
            return (
                <div className="fixed top-20 left-1/2 -translate-x-1/2 z-40 bg-black/50 text-white px-4 py-2 rounded-full shadow-lg text-sm font-semibold backdrop-blur-md border border-white/20">
                    <LucideIcons.MapPin size={16} className="inline mr-2" />
                    {t('momentsFeed.localFeed', { radius: locationFilterRadius })}
                </div>
            );
        } else if (filterType === 'country' && currentUserProfile?.country) {
            return (
                <div className="fixed top-20 left-1/2 -translate-x-1/2 z-40 bg-black/50 text-white px-4 py-2 rounded-full shadow-lg text-sm font-semibold backdrop-blur-md border border-white/20">
                    <LucideIcons.Globe size={16} className="inline mr-2" />
                    {t('momentsFeed.countryFeed', { country: currentUserProfile.country })}
                </div>
            );
        }
        return null;
    };

    // --- Start of Render Logic ---

    // State 1: Handle loading state for local feed specifically
    if (filterType === 'local' && isLocationLoading) {
        return (
            <>
                <ReelItemSkeleton />
                <ReelItemSkeleton />
                <ReelItemSkeleton />
            </>
        );
    }

    // State 2: Handle final error state for local feed
    if (filterType === 'local' && !isLocationLoading && !userLocation) {
        return (
            <div className="fixed inset-0 z-50 bg-black flex items-center justify-center text-center p-4">
                <div className="bg-gray-800 p-8 rounded-lg">
                    <LucideIcons.MapPinOff size={48} className="mx-auto mb-4 text-yellow-400" />
                    <h3 className="text-xl font-bold mb-2">{t('momentsFeed.locationDisabledTitle')}</h3>
                    <p className="text-yellow-400 mb-6">{t('momentsFeed.locationDisabledMessage')}</p>
                    <button 
                        onClick={() => handlePageChange('settings', { section: 'discovery' })} 
                        className="action-button bg-yellow-600 hover:bg-yellow-700 text-lg px-8 py-4"
                    >
                        {t('momentsFeed.goToSettings')}
                    </button>
                </div>
            </div>
        );
    }

    // State 3: Handle general loading state
    if (isLoading && feed.length === 0) return (
        <>
            <ReelItemSkeleton />
            <ReelItemSkeleton />
            <ReelItemSkeleton />
        </>
    );

    if (error) {
        return (
            <div className="fixed inset-0 z-50 bg-black flex items-center justify-center text-center p-4">
                <div className="bg-gray-800 p-8 rounded-lg">
                    <LucideIcons.AlertCircle size={48} className="mx-auto mb-4 text-red-400" />
                    <p className="text-red-400">{error}</p>
                </div>
            </div>
        );
    }

    return (
        <>
            {showReportModal && <ReportModal {...reportContent} onClose={() => setShowReportModal(false)} />}
            <button 
                onClick={() => window.history.back()} 
                className="fixed top-4 left-4 z-50 bg-black/50 text-white p-2 rounded-full hover:bg-black/75 transition-colors"
                aria-label="Go back"
            >
                <LucideIcons.ChevronLeft size={28} />
            </button>
            {visionCreatorOpen && <VisionCreator onClose={() => setVisionCreatorOpen(false)} sharedContent={sharedContent} />}
            <FeedHeader />
            {showNewMomentsButton && (
                <button onClick={scrollToTop} className="fixed top-24 left-1/2 -translate-x-1/2 z-40 bg-purple-600/90 text-white px-4 py-2 rounded-full shadow-lg text-sm font-semibold backdrop-blur-sm animate-bounce">
                    <LucideIcons.ArrowUp size={16} className="inline mr-2" />
                    {t('momentsFeed.newMoments')}
                </button>
            )}
            <div ref={viewerRef} className="moments-viewer">
                {filteredFeed.length > 0 ? (
                    filteredFeed.map((moment, index) => (
                        <MemoizedReelItem
                            key={moment.id + '-' + index}
                            index={index}
                            moment={moment}
                            isActive={index === currentReelIndex}
                            onLike={handleLike}
                            onAmplify={handleAmplify}
                            onUserSelect={handleUserSelect}
                            isMuted={isMuted}
                            onMuteToggle={() => setIsMuted(prev => !prev)}
                            onShareToVision={handleShareToVision}
                            onVisible={handleVisibilityChange}
                            onReport={handleReport}
                        />
                    ))
                ) : (
                    <div className="reel-item text-white text-center flex items-center justify-center" style={{ background: 'linear-gradient(to top, #1a1a1a, #2a2a2a)' }}>
                        <div className="p-10 bg-gray-800/60 rounded-2xl max-w-sm shadow-xl">
                            <LucideIcons.VideoOff size={64} className="mx-auto mb-6 text-purple-400" />
                            <h3 className="text-2xl font-bold mb-2">{t('momentsFeed.noMomentsTitle')}</h3>
                            <p className="mt-2 mb-6 text-gray-300">{t('momentsFeed.noMomentsMessage')}</p>
                            <button onClick={() => handlePageChange('createMoment')} className="action-button bg-purple-600 hover:bg-purple-700 text-lg px-8 py-4">
                                {t('momentsFeed.createMoment')}
                            </button>
                        </div>
                    </div>
                )}
                {isLoading && feed.length > 0 && <LoadingSpinner message={t('momentsFeed.loadingMore')} />}
            </div>
        </>
    );
}

export default MomentsFeedPage;
